import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { StyleSheet, View, Pressable, Platform, useWindowDimensions } from 'react-native';
import { MasonryFlashList } from '@shopify/flash-list';
import { useTheme } from '@/context/ThemeContext';
import { useFileStore, useLightboxStore, useModelStore, useChatInputStore, useAuthStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import StatusContainer from '@/components/StatusContainer';
import MediaCardLarge from '@/components/ChatInput/MediaCardLarge';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import { useRouter } from 'expo-router';
import { BORDER_RADIUS, DEFAULT_VIDEO_MODEL_SLUG } from '@/constants/config';

const createStyles = (colors, isXS) => StyleSheet.create({
  container: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: isXS ? 12 : 18,
    paddingBottom: 20,
  },
  emptyContainer: {
    paddingVertical: 60,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: isXS ? 16 : 24,
  },
  loadingContainer: {
    paddingHorizontal: isXS ? 16 : 24,
    paddingTop: 8,
  },
  loadingGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: isXS ? 8 : 12,
  },
  loadingItem: {
    borderRadius: BORDER_RADIUS.medium || 12,
    backgroundColor: colors.solid_color_secondary,
  },
  itemWrapper: {
    padding: isXS ? 4 : 6,
  },
  itemContainer: {
    borderRadius: BORDER_RADIUS.medium || 12,
    overflow: 'hidden',
    position: 'relative',
  },
  viewChatButton: {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    cursor: 'pointer',
    zIndex: 3,
  },
  footerLoader: {
    paddingVertical: 20,
    alignItems: 'center',
  },
});

const VideoGalleryItem = React.memo(({ file, onPress, onViewChat, isXS }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);
  const [isHovered, setIsHovered] = useState(false);
  
  // Calculate aspect ratio from video metadata
  const videoWidth = file?.metadata?.video?.width || 1920;
  const videoHeight = file?.metadata?.video?.height || 1080;
  const aspectRatio = videoWidth / videoHeight;

  const hasConversation = !!file?.conversationId;

  const handleViewChat = useCallback((e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (e?.preventDefault) e.preventDefault();
    if (onViewChat && file?.conversationId) {
      onViewChat(file.conversationId);
    }
  }, [onViewChat, file?.conversationId]);

  return (
    <View style={styles.itemWrapper}>
      <View 
        style={[styles.itemContainer, { aspectRatio }]}
        onMouseEnter={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
        onMouseLeave={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
      >
        <MediaCardLarge
          fillContainer
          file={file}
          onPress={onPress}
          isDownloadable
        />
        {hasConversation && (
          <Pressable
            style={[
              styles.viewChatButton,
              (Platform.OS === 'web' && isHovered) && { opacity: 1 },
              (Platform.OS !== 'web') && { opacity: 1 },
            ]}
            onPress={handleViewChat}
          >
            <Icon name="message" size={16} color="#fff" />
          </Pressable>
        )}
      </View>
    </View>
  );
});

const VideoGallery = () => {
  const { colors } = useTheme();
  const { isXS, isXL } = useResponsiveness();
  const { t } = useTranslation();
  const router = useRouter();
  const { width: windowWidth } = useWindowDimensions();
  
  const { openLightbox } = useLightboxStore(state => state.actions);
  
  // File store
  const files = useFileStore(state => state.files);
  const generatedVideoIds = useFileStore(state => state.generatedVideoIds);
  const isLoading = useFileStore(state => state.isLoading);
  const errors = useFileStore(state => state.errors);
  const pagination = useFileStore(state => state.pagination);
  
  const { fetchGeneratedVideos } = useFileStore(state => state.actions);
  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);
  const requireAuth = useRequireAuth();

  const numColumns = useMemo(() => {
    if (isXS) return 2;
    if (isXL) return 4;
    return 3;
  }, [isXS, isXL]);

  const gap = isXS ? 8 : 12;
  const horizontalPadding = isXS ? 16 : 24;
  const columnWidth = useMemo(() => {
    const totalGaps = (numColumns - 1) * gap;
    const availableWidth = windowWidth - (horizontalPadding * 2) - totalGaps;
    return availableWidth / numColumns;
  }, [windowWidth, numColumns, gap, horizontalPadding]);

  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  // The "My Videos" gallery is per-user. Skip the fetch entirely for
  // anonymous visitors (would 401) and surface a sign-in CTA empty state
  // below in place of the gallery.
  useEffect(() => {
    if (hasUser) fetchGeneratedVideos(1);
  }, [fetchGeneratedVideos, hasUser]);

  // Get the video files from IDs
  const videoFiles = useMemo(() => {
    return generatedVideoIds.map(id => files[id]).filter(Boolean);
  }, [files, generatedVideoIds]);

  const handleVideoPress = useCallback((file) => {
    if (file?.cdnPath) {
      openLightbox({
        imageUrl: file.cdnPath,
        fileId: file.id,
        conversationId: file.conversationId || null,
        openToEditor: false,
        withInpainting: false,
        withChatInput: false,
      });
    }
  }, [openLightbox]);

  const handleViewChat = useCallback((conversationId) => {
    if (conversationId) {
      router.push(`/c/${conversationId}`);
    }
  }, [router]);

  const handleLoadMore = useCallback(async () => {
    const currentPagination = pagination.generatedVideos;
    if (!currentPagination?.hasMore || isLoading) return;
    
    const nextPage = (currentPagination?.page || 1) + 1;
    await fetchGeneratedVideos(nextPage);
  }, [pagination, isLoading, fetchGeneratedVideos]);

  const handleCreateFirst = useCallback(async () => {
    const ran = requireAuth(() => true, {
      source: 'videos_gallery_create_first',
      title: 'Sign in to make a video',
      subtitle: 'Video generations are billed to your account so we can apply your credits and save the result.',
      icon: 'video',
    });
    if (ran !== true) return;
    // Get the video model - first try store by slug
    let videoModel = useModelStore.getState().getModelBySlug(DEFAULT_VIDEO_MODEL_SLUG);
    
    if (!videoModel) {
      // Fetch video models and find the one with the correct slug
      try {
        await useModelStore.getState().fetchModelsByTag('video');
        videoModel = useModelStore.getState().getModelBySlug(DEFAULT_VIDEO_MODEL_SLUG);
      } catch (error) {
        console.error('Error fetching video models:', error);
      }
    }
    
    if (videoModel?.id) {
      // Initialize video generation with the model pre-selected
      useChatInputStore.getState().initializeVideoGeneration('', videoModel.id, false);
    }
    
    router.push('/app');
  }, [router, requireAuth]);

  const renderItem = useCallback(({ item }) => (
    <VideoGalleryItem
      file={item}
      onPress={handleVideoPress}
      onViewChat={handleViewChat}
      isXS={isXS}
    />
  ), [handleVideoPress, handleViewChat, isXS]);

  const keyExtractor = useCallback((item) => item.id, []);

  const getItemType = useCallback((item) => {
    // Use aspect ratio to group similar items for better recycling
    const videoWidth = item?.metadata?.video?.width || 1920;
    const videoHeight = item?.metadata?.video?.height || 1080;
    const ratio = videoWidth / videoHeight;
    if (ratio > 1.5) return 'landscape';
    if (ratio < 0.75) return 'portrait';
    return 'square';
  }, []);

  const overrideItemLayout = useCallback((layout, item) => {
    // Estimate item height based on aspect ratio for better masonry layout
    const videoWidth = item?.metadata?.video?.width || 1920;
    const videoHeight = item?.metadata?.video?.height || 1080;
    const aspectRatio = videoWidth / videoHeight;
    layout.size = columnWidth / aspectRatio;
  }, [columnWidth]);

  const ListFooterComponent = useCallback(() => {
    if (!pagination?.generatedVideos?.hasMore) return null;
    return (
      <View style={styles.footerLoader}>
        <Text size="s" color="secondary">Loading more...</Text>
      </View>
    );
  }, [pagination, styles.footerLoader]);

  // Show loading state
  if (isLoading && videoFiles.length === 0) {
    const loadingItemWidth = columnWidth;
    return (
      <View style={styles.loadingContainer}>
        <View style={styles.loadingGrid}>
          {[...Array(6)].map((_, index) => (
            <View
              key={index}
              style={[
                styles.loadingItem,
                {
                  width: loadingItemWidth,
                  aspectRatio: 16/9,
                }
              ]}
            />
          ))}
        </View>
      </View>
    );
  }

  // Show error state
  if (errors?.generatedVideos) {
    return (
      <View style={styles.emptyContainer}>
        <StatusContainer
          type="error"
          message={errors.generatedVideos}
          actionText="Retry"
          onAction={() => fetchGeneratedVideos(1)}
        />
      </View>
    );
  }

  // Show empty state — anonymous variant tells the visitor what they're
  // signing up for instead of the generic "no videos yet" copy.
  if (videoFiles.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Icon 
          name="video" 
          size={48} 
          color={colors.text_color_tertiary} 
          style={{ marginBottom: 16 }}
        />
        <Text size="m" weight="medium" color="secondary" style={{ marginBottom: 4 }}>
          {hasUser ? t('videos.noVideos') : 'Sign in to see your videos'}
        </Text>
        <Text size="s" color="tertiary" style={{ marginBottom: 20, textAlign: 'center' }}>
          {hasUser
            ? t('videos.noVideosSubtitle')
            : 'Create a free account so we can save every video you generate to your gallery.'}
        </Text>
        <Button
          text={hasUser ? t('videos.createFirst') : 'Sign up free'}
          variant="primary"
          size="medium"
          prependIcon={hasUser ? 'sparkles' : 'user-plus'}
          onPress={handleCreateFirst}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <MasonryFlashList
        data={videoFiles}
        numColumns={numColumns}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        getItemType={getItemType}
        overrideItemLayout={overrideItemLayout}
        estimatedItemSize={columnWidth * 0.6}
        contentContainerStyle={styles.listContent}
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.5}
        ListFooterComponent={ListFooterComponent}
        scrollEnabled={Platform.OS !== 'macos'}
        showsVerticalScrollIndicator={false}
        optimizeItemArrangement
      />
    </View>
  );
};

export default React.memo(VideoGallery);
